import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Endocrine subsection adapter
 * Handles endocrine system assessment and menstrual cycle tracking (5 fields)
 */
export class EndocrineAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Endocrine assessment fields
    const endocrineWnl = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_WNL
    )?.at(0)

    const menstrualCycle = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_CYCLE
    )?.at(0)

    const menstrualDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_DATE
    )?.at(0)

    const endocrineNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RocFormField.ENDOCRINE_WNL]: endocrineWnl,
      [RocFormField.ENDOCRINE_MENSTRUAL_CYCLE]: menstrualCycle,
      [RocFormField.ENDOCRINE_MENSTRUAL_DATE]: menstrualDate,
      [RocFormField.ENDOCRINE_NOTES]: endocrineNotes,
      [RocFormField.ENDOCRINE_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.ENDOCRINE_WNL)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_WNL,
        value: this.getFormDataField(RocFormField.ENDOCRINE_WNL)
      })
    }

    if (this.formDataFieldExists(RocFormField.ENDOCRINE_MENSTRUAL_CYCLE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_CYCLE,
        value: this.getFormDataField(RocFormField.ENDOCRINE_MENSTRUAL_CYCLE)
      })
    }

    if (this.formDataFieldExists(RocFormField.ENDOCRINE_MENSTRUAL_DATE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_DATE,
        value: this.getFormDataField(RocFormField.ENDOCRINE_MENSTRUAL_DATE)
      })
    }

    if (this.formDataFieldExists(RocFormField.ENDOCRINE_NOTES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_NOTES,
        value: this.getFormDataField(RocFormField.ENDOCRINE_NOTES)
      })
    }

    if (this.formDataFieldExists(RocFormField.ENDOCRINE_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_SYSTEM_SUMMARY,
        value: this.getFormDataField(RocFormField.ENDOCRINE_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
